import { CARD_STATE } from './listingCardState';

/**
 * Everything a seller can do to one advertisement.
 *
 * These are the only actions; there is no second place to find them. Each carries
 * its billing consequence as a subtitle, which is the whole reason the subtitle
 * exists — the support complaint behind this redesign was that nobody could tell
 * which action stops the money.
 */
/**
 * Paid promotions are dormant. When they come back, add `PROMOTE: 'promote'` here,
 * a COPY entry, and the key to ITEMS_BY_STATE for the states where an advertisement
 * is actually visible to buyers (ONLINE, NEGOTIATION, CANCEL_SCHEDULED) — promoting
 * a draft or an offline listing buys nothing.
 *
 * It belongs immediately after the sale-status toggle and *before* the divider:
 * it costs money, but it is additive rather than an ending, so it does not read with
 * stop/cancel/delete. Note the current design deliberately omits it — see
 * docs/design_handoff_manage_listing_card/README.md — so re-adding it is a product
 * decision, not just a code change. See also PROMOTIONS_REVERT_NOTES.md.
 */
export const MENU_ACTION = {
  EDIT: 'edit',
  VIEW: 'view',
  NEGOTIATE: 'negotiate',
  FOR_SALE: 'forSale',
  PUBLISH: 'publish',
  REPUBLISH: 'republish',
  STOP: 'stop',
  SUBSCRIPTION: 'sub',
  RESUME: 'resume',
  RENEW: 'renew',
  DELETE: 'del',
  DELETE_PAID: 'delPaid',
};

/**
 * Which items appear, per state, in the order they are shown.
 *
 * Order is deliberate and fixed: harmless first, money and endings last. Invalid
 * actions are absent rather than disabled — a disabled row invites the seller to
 * wonder what they did wrong.
 */
const ITEMS_BY_STATE = {
  [CARD_STATE.BLANK]: [MENU_ACTION.EDIT, MENU_ACTION.DELETE],
  [CARD_STATE.DRAFT]: [MENU_ACTION.EDIT, MENU_ACTION.VIEW, MENU_ACTION.DELETE],
  [CARD_STATE.DRAFT_PAID]: [
    MENU_ACTION.EDIT,
    MENU_ACTION.VIEW,
    MENU_ACTION.PUBLISH,
    MENU_ACTION.SUBSCRIPTION,
    MENU_ACTION.DELETE_PAID,
  ],
  [CARD_STATE.ONLINE]: [
    MENU_ACTION.EDIT,
    MENU_ACTION.VIEW,
    MENU_ACTION.NEGOTIATE,
    MENU_ACTION.STOP,
    MENU_ACTION.SUBSCRIPTION,
  ],
  [CARD_STATE.NEGOTIATION]: [
    MENU_ACTION.EDIT,
    MENU_ACTION.VIEW,
    MENU_ACTION.FOR_SALE,
    MENU_ACTION.STOP,
    MENU_ACTION.SUBSCRIPTION,
  ],
  // The negotiation toggle is deliberately absent while a cancellation is pending:
  // haggling over a listing that is about to come down is not a state worth offering.
  [CARD_STATE.CANCEL_SCHEDULED]: [
    MENU_ACTION.EDIT,
    MENU_ACTION.VIEW,
    MENU_ACTION.RESUME,
    MENU_ACTION.STOP,
    MENU_ACTION.SUBSCRIPTION,
  ],
  // "View as buyer" stays as a private preview even though buyers cannot see it.
  [CARD_STATE.OFFLINE]: [
    MENU_ACTION.EDIT,
    MENU_ACTION.VIEW,
    MENU_ACTION.REPUBLISH,
    MENU_ACTION.STOP,
    MENU_ACTION.SUBSCRIPTION,
  ],
  // Renewing is the only thing that changes anything, so it leads. Stop, republish
  // and the sale status are all absent: without a subscription nothing can go online.
  [CARD_STATE.EXPIRED]: [MENU_ACTION.RENEW, MENU_ACTION.EDIT, MENU_ACTION.SUBSCRIPTION],
  [CARD_STATE.SOLD]: [],
  [CARD_STATE.LOCKED]: [],
};

/** Items that carry a money consequence — the divider opens on the first of these. */
const MONEY_GROUP = [
  MENU_ACTION.STOP,
  MENU_ACTION.SUBSCRIPTION,
  MENU_ACTION.RESUME,
  MENU_ACTION.RENEW,
  MENU_ACTION.DELETE,
  MENU_ACTION.DELETE_PAID,
];

const DESTRUCTIVE = [MENU_ACTION.DELETE, MENU_ACTION.DELETE_PAID];

/** Business sellers have no per-listing subscription, so none of this applies to them. */
const SUBSCRIPTION_ONLY = [
  MENU_ACTION.SUBSCRIPTION,
  MENU_ACTION.RESUME,
  MENU_ACTION.RENEW,
  MENU_ACTION.DELETE_PAID,
];

/**
 * Translation keys per item. Business sellers get a different subtitle wherever the
 * default one talks about a subscription they do not have.
 */
const COPY = {
  [MENU_ACTION.EDIT]: { label: 'edit.label', subtitle: 'edit.subtitle' },
  [MENU_ACTION.VIEW]: { label: 'view.label', subtitle: 'view.subtitle' },
  [MENU_ACTION.NEGOTIATE]: {
    label: 'negotiate.label',
    subtitle: 'negotiate.subtitle',
    businessSubtitle: 'negotiate.subtitleBusiness',
  },
  [MENU_ACTION.FOR_SALE]: {
    label: 'forSale.label',
    subtitle: 'forSale.subtitle',
    businessSubtitle: 'forSale.subtitleBusiness',
  },
  [MENU_ACTION.PUBLISH]: { label: 'publish.label', subtitle: 'publish.subtitle' },
  [MENU_ACTION.REPUBLISH]: { label: 'republish.label', subtitle: 'republish.subtitle' },
  [MENU_ACTION.STOP]: {
    label: 'stop.label',
    subtitle: 'stop.subtitle',
    businessSubtitle: 'stop.subtitleBusiness',
  },
  [MENU_ACTION.SUBSCRIPTION]: { label: 'sub.label', subtitle: 'sub.subtitle' },
  [MENU_ACTION.RESUME]: { label: 'resume.label', subtitle: 'resume.subtitle' },
  [MENU_ACTION.RENEW]: { label: 'renew.label', subtitle: 'renew.subtitle' },
  [MENU_ACTION.DELETE]: { label: 'del.label', subtitle: 'del.subtitle' },
  [MENU_ACTION.DELETE_PAID]: { label: 'delPaid.label', subtitle: 'delPaid.subtitle' },
};

const TRANSLATION_PREFIX = 'ListingActionsMenu';

/**
 * The menu for one advertisement, as descriptors the component can render blindly.
 *
 * @param {Object} args
 * @param {string} args.state a CARD_STATE
 * @param {boolean} [args.isBusinessSeller]
 * @returns {Array<{ key, labelId, subtitleId, isDestructive, startsMoneyGroup }>}
 */
export const deriveMenuItems = ({ state, isBusinessSeller = false }) => {
  const items = ITEMS_BY_STATE[state] || [];

  const applicable = isBusinessSeller
    ? items.filter(key => !SUBSCRIPTION_ONLY.includes(key))
    : items;

  let moneyGroupOpened = false;

  return applicable.map(key => {
    const copy = COPY[key];
    const subtitleKey =
      isBusinessSeller && copy.businessSubtitle ? copy.businessSubtitle : copy.subtitle;

    const startsMoneyGroup = !moneyGroupOpened && MONEY_GROUP.includes(key);
    if (startsMoneyGroup) {
      moneyGroupOpened = true;
    }

    return {
      key,
      labelId: `${TRANSLATION_PREFIX}.${copy.label}`,
      subtitleId: `${TRANSLATION_PREFIX}.${subtitleKey}`,
      isDestructive: DESTRUCTIVE.includes(key),
      startsMoneyGroup,
    };
  });
};

/** Whether the ⋯ button should exist at all. */
export const hasAnyMenuItems = ({ state, isBusinessSeller = false }) =>
  deriveMenuItems({ state, isBusinessSeller }).length > 0;

/**
 * The single action that gets the advertisement moving, shown as a footer button in
 * the states where exactly one action matters. A draft nobody finishes is the
 * expensive failure, so it is worth one control outside the menu.
 */
export const derivePrimaryAction = ({ state }) => {
  switch (state) {
    case CARD_STATE.BLANK:
      return { key: MENU_ACTION.EDIT, labelId: `${TRANSLATION_PREFIX}.primary.finishDraft` };
    case CARD_STATE.DRAFT:
      return { key: MENU_ACTION.EDIT, labelId: `${TRANSLATION_PREFIX}.primary.completeDraft` };
    case CARD_STATE.DRAFT_PAID:
      return { key: MENU_ACTION.PUBLISH, labelId: `${TRANSLATION_PREFIX}.primary.publish` };
    case CARD_STATE.EXPIRED:
      return { key: MENU_ACTION.RENEW, labelId: `${TRANSLATION_PREFIX}.primary.renew` };
    default:
      return null;
  }
};
